import { AnalysisItem, AnalysisType, GroupKey, GroupType, projectAnalysis, ProjectAnalysisEvents, UnitType } from "@/types/analysis/ProjectAnalysis";
import { ColorDefinition } from "@/types/colors";
import { useProjectData } from "@api/analysis";
import { AppSettingType, CadModelCategory, FeatureManagementType } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import DataGrid from "@components/DataGrid/DataGrid";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import SquareIcon from "@mui/icons-material/Square";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { GridCellParams, GridColDef, GridColumnHeaderParams, GridInputRowSelectionModel } from "@mui/x-data-grid-premium";
import sortArrays from "@utils/sortArrays";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ResultsEvent, ResultsEvents } from "../../ResultsEvents";
import MicroChip from "@components/MicroChip/MicroChip";

export interface ByCategoriesDataGridProps {
  emitter: Emitter<Record<EventType, unknown>>;
}

type MyGridColDef = GridColDef & {
  id?: number;
  groupKey?: GroupKey;
  name?: string;
  scenarioName?: string;
  modelName?: string;
};

const ByCategoriesDataGrid = observer((props: ByCategoriesDataGridProps) => {
  console.log("ByCategoriesDataGrid: ", props);
  const appStore = useAppStore();
  const { id: projectId } = useParams();
  const cadModelCategories = useCadModelCategories();
  const [rows, setRows] = useState<MyGridColDef[]>([]);
  const [columns, setColumns] = useState<MyGridColDef[]>([]);
  const [aggregationModel, setAggregationModel] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>([]);
  const [rowHighlights, setRowHighlights] = useState<Set<GroupKey>>(new Set());
  const projectData = useProjectData(projectId!);

  useEffect(() => {
    updateGridData();
    updateGridHighlights();
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.ANALYSIS_CHANGED_EVENT, updateGridData);
    };
  }, []);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SELECTION_CHANGED_EVENT, updateGridHighlights);
    };
  }, []);

  useEffect(() => {
    props.emitter.on(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);

    return () => {
      props.emitter.off(ResultsEvents.SELECTION_CHANGED, handleUpdateSelection);
    };
  }, [rows]);

  useEffect(() => {
    projectAnalysis.emitter.on(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateGridData); // Re-calculate grid data with new units
 
    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.UNIT_SELECTION_CHANGED_EVENT, updateGridData);
    };
  }, []);

  const handleUpdateSelection = async (event: any) => {
    if (event.key === "geometry-loaded") {
      updateViewers(event.groupKey);
    } else if (event.key !== "data-grid") {
      updateSelection(event.groupKey);
    }
  };

  const updateGridHighlights = async () => {
    const highlights = projectAnalysis.getSelectedElementsByAnalysisType(appStore.selectionManager, projectData.data, AnalysisType.CadModelCategory) || new Set();
    setRowHighlights(highlights);
  };

  const updateSelection = (groupKey?: number | string) => {
    if (groupKey) {
      const row = rows.find((r: MyGridColDef) => r.name === groupKey);
      if (row) {
        setRowSelectionModel([row.id!]);
        return;
      }
    }

    setRowSelectionModel([]);
  };

  const updateGridData = async () => {
    const analysisData = projectAnalysis.getAnalysis(AnalysisType.CadModelCategory);
    if (analysisData) {
      const aggregationModel: any = {};
      const keys = analysisData.getGroupKeys();
      let newRows = keys.map((key, index) => ({ id: index, groupKey: key, name: "" })) as MyGridColDef[];
      let s = 0;
      let i = 1;
      const newColDefs: MyGridColDef[] = [...colDefs];
      analysisData.scenarioAnalyses.forEach((scenarioAnalysis) => {
        scenarioAnalysis.groupData.forEach((groupData) => {
          s = scenarioAnalysis.scenarioIndex + 1;
          newColDefs.push({
            field: `col${i}`,
            headerName: `${i18n.t("results-tab.title.quantity")}`,
            flex: 1,
            type: "number",
            minWidth: 100,
          });
          aggregationModel[`col${i}`] = "sum";
          Array.from(groupData.items.values()).forEach((item: AnalysisItem, index) => {
            newRows[index] = {
              ...newRows[index],
              [`col${i}`]: item.elementCount,
            };
          });
          i++;
          newColDefs.push({
            field: `col${i}`,
            scenarioName: scenarioAnalysis.name,
            modelName: groupData.name,
            headerName: projectAnalysis.groupType === GroupType.Model ? `S${s}-M${groupData.groupIndex + 1}` : `S${s}`,
            renderHeader,
            valueFormatter: renderFormattedNumber,
            flex: 1,
            type: "number",
            minWidth: 150,
          });
          aggregationModel[`col${i}`] = "sum";
          Array.from(groupData.items.values()).forEach((item: AnalysisItem, index) => {
            let convertedValue = item.co2eTotal;

            // Apply unit conversion if AdditionalMetadata feature is enabled
            if(appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
              convertedValue = projectAnalysis.applyUnitConversion(item.co2eTotal, scenarioAnalysis);
            }

            newRows[index] = {
              ...newRows[index],
              name: item.name,
              [`col${i}`]: convertedValue,
            };
          });
          i++;
        });
        s++;
      });

      // Sort newRows by id in descending order
      const gridColors = analysisData.colorDefinitions();
      const sorted = sortArrays({ newRows, gridColors }, (a, b) => b.id! - a.id!) as { newRows: MyGridColDef[]; gridColors: ColorDefinition[] };
      newRows = sorted.newRows.map((value: MyGridColDef, index: number) => ({ ...value, color: sorted.gridColors[index].hex }));

      setColumns([...newColDefs]);
      setRows(newRows);
      setAggregationModel(aggregationModel);
    } else {
      setRows([]);
      setAggregationModel({});
    }
  };

  const renderHeader = (params: GridColumnHeaderParams) => {
    const { colDef }: { colDef: MyGridColDef } = params;
    
    const getUnitDisplay = () => {
      const unitKey = 'results-tab.title.co2e'; //Default unit key
      if(appStore.hasFeature(FeatureManagementType.AdditionalMetadata)) {
        switch(projectAnalysis.selectedUnitType) {
          case UnitType.KgCO2ePerM2:
            return i18n.t("results-tab.title.unit-kgco2e-m2");
          case UnitType.KgCO2ePerM2PerYear:
            return i18n.t("results-tab.title.unit-kgco2e-m2-year");
          case UnitType.tCO2e:
            return i18n.t(unitKey);
        }
      }
      return i18n.t(unitKey);
    }
    
    return (
      <Tooltip
        title={
          <>
            <Typography variant="caption">{colDef.scenarioName}</Typography>
            {projectAnalysis.groupType === GroupType.Model && <Typography variant="subtitle1">{colDef.modelName}</Typography>}
          </>
        }
      >
        <Box>
          <MicroChip label={`${params.colDef.headerName}`} />
          <Box ml={1} component={"span"}>
            {getUnitDisplay()}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const renderFormattedNumber = (value?: number) => {
    if (value === undefined || value === null) {
      return "";
    }

    return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
  };

  const colDefs: MyGridColDef[] = [
    {
      field: "name",
      headerName: i18n.t("results-tab.title.categories"),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridCellParams) => {
        if (params.id === "auto-generated-group-footer-root") {
          return <></>;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SquareIcon sx={{ color: params.row.color }} />
            <Box ml={1}>{params.row.name}</Box>
          </Box>
        );
      },
    },
  ];

  const updateViewers = async (groupKey?: string | undefined) => {
    if (groupKey) {
      const cadModelCategory = cadModelCategories.data?.find((cmc: CadModelCategory) => cmc.label === groupKey);
      await appStore.lmViewer1?.isolateByModelCategoryId(cadModelCategory?.value);
      await appStore.lmViewer2?.isolateByModelCategoryId(cadModelCategory?.value);
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }
  };

  const handleRowSelection = async (colIndex?: number | undefined, groupKey?: string | undefined) => {
    console.log(`updateViewers: ${colIndex}, ${groupKey}`);

    await updateViewers(groupKey);

    const event: ResultsEvent = {
      key: "data-grid",
      eventType: ResultsEvents.SELECTION_CHANGED,
      groupKey,
    };

    props.emitter.emit(ResultsEvents.SELECTION_CHANGED, event);
  };

  return (
    <Box sx={{ display: "flex", flexGrow: 1, flexShrink: 0, position: "relative", height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row: any) => row.id}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "calc(100% - 8px)",
          height: "100%",
        }}
        getRowClassName={(params) => (rowHighlights.has(params.row.groupKey) ? "app-theme--active" : "")}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(selection) => {
          setRowSelectionModel(selection);
          if (selection.length > 0) {
            const row = rows.find((r: any) => r.id === selection[0]);
            handleRowSelection(row?.id, row?.name);
          }
        }}
        aggregationModel={aggregationModel}
      />
    </Box>
  );
});

export default ByCategoriesDataGrid;
